import { TFunction } from 'i18next';
import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import {
  Alert,
  InputControl,
  MaskedTextInput,
  PhoneNumberInput,
  TextInput,
  TextInputType,
} from '@growtherapy/sprout-ui';

import { PII_VALIDATORS } from '../utils';

export type PIIVerificationFormState = {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string; // MM/DD/YYYY format (user input)
  phoneNumber: string;
};

export type PIIVerificationFormField = keyof PIIVerificationFormState;

type FieldErrors = Record<PIIVerificationFormField, string>;

function isPIIFormValid(t: TFunction, formState: PIIVerificationFormState): boolean {
  return Object.entries(formState).every(([field, value]) => {
    const [isValid] = PII_VALIDATORS[field as PIIVerificationFormField](t, value);
    return isValid;
  });
}

export type PIIVerificationProps = {
  formState: PIIVerificationFormState;
  onFormStateChange: (state: PIIVerificationFormState) => void;
  onIsValidChange: (isValid: boolean) => void;
  onSubmit: (formState: PIIVerificationFormState) => void;
  serverError?: string | null;
};

export function PIIVerification({
  formState,
  onFormStateChange,
  onIsValidChange,
  onSubmit,
  serverError,
}: PIIVerificationProps) {
  const t = useTranslate();
  const { firstName, lastName, email, dateOfBirth, phoneNumber } = formState;

  const updateField = useCallback(
    (field: keyof PIIVerificationFormState, value: string) => {
      onFormStateChange({ ...formState, [field]: value });
    },
    [formState, onFormStateChange],
  );

  // Client-side validation errors (shown on blur)
  const [errors, setErrors] = useState<FieldErrors>({
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: '',
    phoneNumber: '',
  });

  // Track which fields have been touched (for showing errors on blur)
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const isFormValid = useMemo(() => isPIIFormValid(t, formState), [t, formState]);

  // Notify parent if validity changes
  useEffect(
    function notifyParentOfValidityChange() {
      onIsValidChange(isFormValid);
    },
    [isFormValid, onIsValidChange],
  );

  // Handle blur - mark as touched, validate, and show any error
  const handleBlur = useCallback(
    (field: keyof FieldErrors, value: string) => {
      setTouched((prev) => ({ ...prev, [field]: true }));
      const [isValid, error] = PII_VALIDATORS[field as PIIVerificationFormField](t, value);
      setErrors((prev) => ({ ...prev, [field]: isValid ? '' : error }));
    },
    [t],
  );

  // Handle change - update field, validate + show any error if field is touched
  const handleChange = useCallback(
    (field: keyof FieldErrors, value: string) => {
      updateField(field, value);
      // Only update error if field is touched -- this prevents over-aggressive
      // error feedback while the user is filling in an initial value.
      if (touched[field]) {
        const [isValid, error] = PII_VALIDATORS[field as PIIVerificationFormField](t, value);
        setErrors((prev) => ({ ...prev, [field]: isValid ? '' : error }));
      }
    },
    [t, touched, updateField],
  );

  const validateAndSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!isFormValid) return;
      onSubmit(formState);
    },
    [isFormValid, formState, onSubmit],
  );

  return (
    <form className='flex flex-col gap-350 w-full' onSubmit={validateAndSubmit}>
      <span className='hidden' id='sr-form-instructions'>
        {t('telehealth-core:introRoom.enterYourDetailsToVerify')}
      </span>
      {serverError && (
        <Alert className='mb-250' use='error' heading={t('telehealth-core:introRoom.weCouldNotVerifyYourIdentity')}>
          {serverError}
        </Alert>
      )}
      {/* First Name */}
      <InputControl
        className='mb-0'
        label={t('telehealth-core:clientInfo.clientFirstName')}
        error={touched.firstName ? errors.firstName : undefined}
        required
      >
        <TextInput
          // This field is the first focusable input field on the page and is
          // part of a form that is the primary purpose of that page.
          // Autofocusing it will not skip over any other important meaning or
          // context on the page, so this is a situation where it's appropriate
          // and good UX to autofocus it.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          aria-describedby='sr-form-instructions'
          value={firstName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('firstName', e.target.value)}
          onBlur={() => handleBlur('firstName', firstName)}
          error={touched.firstName && !!errors.firstName}
          mask='exclude'
          placeholder={t('telehealth-core:formCore.firstName')}
          name='firstName'
          autoComplete='given-name'
        />
      </InputControl>

      {/* Last Name */}
      <InputControl
        className='mb-0'
        label={t('telehealth-core:clientInfo.clientLastName')}
        error={touched.lastName ? errors.lastName : undefined}
        required
      >
        <TextInput
          value={lastName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('lastName', e.target.value)}
          onBlur={() => handleBlur('lastName', lastName)}
          error={touched.lastName && !!errors.lastName}
          mask='exclude'
          placeholder={t('telehealth-core:formCore.lastName')}
          name='lastName'
          autoComplete='family-name'
        />
      </InputControl>

      {/* Email */}
      <InputControl
        className='mb-0'
        label={t('telehealth-core:formCore.email')}
        error={touched.email ? errors.email : undefined}
        required
      >
        <TextInput
          value={email}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('email', e.target.value)}
          onBlur={() => handleBlur('email', email)}
          error={touched.email && !!errors.email}
          mask='exclude'
          placeholder={t('telehealth-core:formCore.emailExampleCom')}
          name='email'
          type={TextInputType.EMAIL}
          autoComplete='email'
        />
      </InputControl>

      {/* Date of Birth */}
      <InputControl
        className='mb-0'
        label={t('telehealth-core:formCore.dateOfBirth')}
        error={touched.dateOfBirth ? errors.dateOfBirth : undefined}
        required
      >
        <MaskedTextInput
          inputMask='99/99/9999'
          value={dateOfBirth}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('dateOfBirth', e.target.value)}
          onBlur={() => handleBlur('dateOfBirth', dateOfBirth)}
          error={touched.dateOfBirth && !!errors.dateOfBirth}
          mask='exclude'
          placeholder={t('telehealth-core:formCore.MMDDYYYY')}
          name='dateOfBirth'
          inputMode='numeric'
          autoComplete='bday'
        />
      </InputControl>

      {/* Last Four Digits of Phone Number */}
      <InputControl
        className='mb-0'
        label={t('telehealth-core:formCore.phoneNumber')}
        error={touched.phoneNumber ? errors.phoneNumber : undefined}
        required
      >
        <PhoneNumberInput
          value={phoneNumber}
          onChange={(value: string) => handleChange('phoneNumber', value)}
          onBlur={() => handleBlur('phoneNumber', phoneNumber)}
          error={touched.phoneNumber && !!errors.phoneNumber}
          mask='exclude'
          name='phoneNumber'
          autoComplete='tel'
        />
      </InputControl>

      {/* Hidden submit button enables Enter key submission from any field */}
      <button type='submit' className='sr-only' tabIndex={-1}>
        {t('telehealth-core:core.submit')}
      </button>
    </form>
  );
}
